import React, { useState, useRef, useEffect } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";

// Turn the on-screen crop selection into a JPEG Blob at full source resolution.
// ReactCrop reports the crop in displayed-image pixels, so we scale back up to
// the image's natural size before drawing. Quality is kept high here (0.92) on
// purpose — the parent still runs this through compressImage() (max 800px, 0.7),
// so the final upload is what actually shrinks; cropping early would lose detail
// twice.
function getCroppedBlob(image, crop) {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = Math.max(1, Math.round(crop.width * scaleX));
  canvas.height = Math.max(1, Math.round(crop.height * scaleY));

  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
}

const formatKB = (bytes) => `${(bytes / 1024).toFixed(0)} KB`;

// App brand teal — same colour the report tables use for their header rows, so
// the crop dialog sits in the same visual family instead of MUI's default blue.
const BRAND = "#1e5f5b";

// Gold is the report screen's action-button colour (Save/Prev/Next). We reuse it
// for the primary crop action and for the header title so the dialog matches the
// screen's teal + gold theme. Button text is white to match those existing
// buttons exactly (their established look).
const GOLD = "#efc71d";
const GOLD_HOVER = "#d9b417";
const BTN_TEXT = "#ffffff";

// Steps through every picked image one at a time so the user can crop (or keep)
// each before it gets uploaded. Rendered only while a batch is in progress, so
// it always starts from a clean slate (no need to reset state on close).
const ImageCropModal = ({ open, files, onCancel, onComplete }) => {
  const [index, setIndex] = useState(0);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);

  const file = files && files[index];
  const [src, setSrc] = useState(null);
  // The <img> only fails to load when the browser can't decode the format
  // (e.g. HEIC/TIFF outside Safari). We don't block these up front — Safari
  // handles them fine — but if the preview broke here, cropping/compressing it
  // would silently fail, so we flag it and stop the action with a message.
  const [decodeFailed, setDecodeFailed] = useState(false);

  // Create the object URL inside the effect (not useMemo): React 18 StrictMode
  // double-invokes effects in dev, so a single memoized URL would get revoked by
  // the first cleanup before the <img> can load it — leaving a broken image.
  // Making a fresh URL each run means the surviving one is always live.
  useEffect(() => {
    setDecodeFailed(false);
    if (!file) {
      setSrc(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!open || !file) return null;

  const titleId = "image-crop-dialog-title";

  // Seed the selection to the whole image so the corner handles sit exactly on
  // the image's real corners, and a single click of "Crop & Save" keeps the
  // full picture even if the user never drags a handle.
  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop({ unit: "%", x: 0, y: 0, width: 100, height: 100 });
    setCompletedCrop({ unit: "px", x: 0, y: 0, width, height });
  };

  const advance = (blob) => {
    const next = [...results, blob];
    if (index + 1 < files.length) {
      setResults(next);
      setIndex(index + 1);
      setCrop(undefined);
      setCompletedCrop(undefined);
      setBusy(false);
    } else {
      setBusy(false);
      onComplete(next);
    }
  };

  // Shared gate for both actions: if the browser couldn't decode this image
  // there's nothing to crop or compress, so tell the user instead of failing
  // silently. Returns true when it's safe to proceed.
  const ensureDecodable = () => {
    if (decodeFailed) {
      toast.error(
        `${file.name} can't be processed in this browser. This image format only works in Safari — please open the report in Safari, or upload a JPG/PNG instead.`
      );
      return false;
    }
    return true;
  };

  const handleCrop = async () => {
    if (!ensureDecodable()) return;
    if (!imgRef.current || !completedCrop || !completedCrop.width) {
      // Nothing selected — fall back to the original file.
      advance(file);
      return;
    }
    setBusy(true);
    const blob = await getCroppedBlob(imgRef.current, completedCrop);
    advance(blob || file);
  };

  const handleUseOriginal = () => {
    if (!ensureDecodable()) return;
    advance(file);
  };

  return (
    <Dialog
      open={open}
      // Don't let a stray click outside discard a half-done crop batch — only
      // Cancel / Escape should close. (MUI fires onClose with reason
      // "backdropClick" for outside clicks, "escapeKeyDown" for the Esc key.)
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") onCancel();
      }}
      maxWidth="md"
      fullWidth
      aria-labelledby={titleId}
      // The report itself is an MUI <Modal>; sit clearly above it so the crop
      // dialog is the only thing in focus.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 5 }}
      // A global stylesheet (PreviewReportModal.css) forces every MUI dialog
      // backdrop transparent and strips Paper shadows. That makes the report
      // behind this dialog bleed through and clash with these buttons. Re-assert
      // a real scrim + shadow here (with !important to beat that global rule) so
      // the dialog reads as a proper top layer.
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6) !important",
            backdropFilter: "blur(2px) !important",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: "10px",
          m: 2,
          maxHeight: "calc(100% - 32px)",
          boxShadow: "0 14px 44px rgba(0, 0, 0, 0.4) !important",
        },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{ backgroundColor: BRAND, color: GOLD, fontWeight: 600 }}
      >
        Crop image {index + 1} of {files.length}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Drag the handles to crop, or keep the original. Images are compressed
            automatically before upload.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {file.name} — original {formatKB(file.size)}
          </Typography>
          <Box
            sx={{
              maxHeight: "60vh",
              overflow: "auto",
              // react-image-crop draws 8 handles by default: 4 corners and one
              // at the midpoint of each side. The side-midpoint ones look like
              // stray handles floating in the middle of each edge, so hide them
              // and keep only the corner handles.
              "& .ReactCrop__drag-handle.ord-n, & .ReactCrop__drag-handle.ord-e, & .ReactCrop__drag-handle.ord-s, & .ReactCrop__drag-handle.ord-w":
                { display: "none" },
            }}
          >
            {src ? (
              <ReactCrop
                crop={crop}
                onChange={(_pixelCrop, percentCrop) => setCrop(percentCrop)}
                onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
                keepSelection
              >
                <img
                  ref={imgRef}
                  src={src}
                  alt={`${file.name}, ready to crop`}
                  style={{ maxWidth: "100%", display: "block" }}
                  onLoad={onImageLoad}
                  onError={() => setDecodeFailed(true)}
                />
              </ReactCrop>
            ) : (
              <CircularProgress size={24} />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onCancel} color="inherit" disabled={busy}>
          Cancel
        </Button>
        <Button onClick={handleUseOriginal} disabled={busy} sx={{ color: BRAND }}>
          Use original
        </Button>
        <Button
          onClick={handleCrop}
          variant="contained"
          disabled={busy}
          sx={{
            backgroundColor: GOLD,
            color: BTN_TEXT,
            fontWeight: 700,
            "&:hover": { backgroundColor: GOLD_HOVER },
          }}
        >
          {busy ? (
            <CircularProgress size={20} />
          ) : index + 1 < files.length ? (
            "Crop & next"
          ) : (
            "Crop & save"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropModal;
